import { error as logRocketError } from 'logrocket';
import { CanvasImageSourceWithoutSVG, getImageDimensions } from 'src/react/_util/roomImages/getImageDimensions';
import { getResizedImageDimensions } from 'src/react/_util/roomImages/getResizedImageDimensions';
import { isDevEnvironment } from '../../../util/env';

const AI_SNAPSHOT_FORCE_WIDTH = 480;
const AI_SNAPSHOT_FORCE_HEIGHT = 640;

export async function createCameraStream(): Promise<MediaStream> {
  const videoProperties: MediaTrackConstraints = {
    facingMode: 'environment',
  };

  try {
    return await window.navigator.mediaDevices.getUserMedia({
      audio: false,
      video: videoProperties,
    });
  } catch (e: unknown) {
    const error = e as { constraint: string; name: string };
    const shouldRetry = isDevEnvironment && error.constraint === 'facingMode';
    if (!shouldRetry) {
      throw e;
    }
  }

  // Retry with default camera if no rear camera
  delete videoProperties.facingMode;

  return await window.navigator.mediaDevices.getUserMedia({
    audio: false,
    video: videoProperties,
  });
}

export function shutdownMediaStream(mediaStream: MediaStream): void {
  mediaStream.getTracks().forEach((track) => track.stop());
}

type CameraSupportResult = { isSupported: boolean; errorReason: string };

let CAMERA_SUPPORT_RESULT: CameraSupportResult | undefined;

export async function checkCameraSupport(): Promise<CameraSupportResult> {
  if (!CAMERA_SUPPORT_RESULT) {
    try {
      const mediaStream = await createCameraStream();
      shutdownMediaStream(mediaStream);
      CAMERA_SUPPORT_RESULT = {
        isSupported: true,
        errorReason: '',
      };
    } catch (e: unknown) {
      const error = e as { constraint: string; name: string };
      let errorReason;

      if (error.constraint === 'facingMode') {
        errorReason = 'no rear camera';
        CAMERA_SUPPORT_RESULT = {
          isSupported: false,
          errorReason,
        };
      } else if (error.name === 'NotAllowedError') {
        errorReason = 'no camera access';
      } else {
        logRocketError('Failed to initialize camera', e);
        errorReason = 'error getting camera';
      }

      return {
        isSupported: false,
        errorReason,
      };
    }
  }

  return CAMERA_SUPPORT_RESULT;
}

type BlobForAIWithDimensions = {
  width: number;
  height: number;
  blob: Blob;
};

export const createImageBlobForAI = async (
  image: CanvasImageSourceWithoutSVG,
  imageQuality?: number
): Promise<BlobForAIWithDimensions> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Your browser does not support CanvasRendering2DContext');

  let { width: snapshotWidth, height: snapshotHeight } = getImageDimensions(image);

  const isVertical = snapshotWidth < snapshotHeight;
  const defaultAspectRatio = AI_SNAPSHOT_FORCE_WIDTH / AI_SNAPSHOT_FORCE_HEIGHT;
  const currentAspectRatio = snapshotWidth / snapshotHeight;
  const shouldBeStretched = isVertical && currentAspectRatio < defaultAspectRatio;

  if (shouldBeStretched) {
    snapshotHeight = AI_SNAPSHOT_FORCE_HEIGHT;
    snapshotWidth = AI_SNAPSHOT_FORCE_WIDTH;
  } else {
    ({ width: snapshotWidth, height: snapshotHeight } = getResizedImageDimensions(
      image,
      AI_SNAPSHOT_FORCE_WIDTH,
      AI_SNAPSHOT_FORCE_HEIGHT
    ));
  }

  canvas.width = snapshotWidth;
  canvas.height = snapshotHeight;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return await new Promise((success, reject) =>
    canvas.toBlob(
      (blob) => {
        if (blob) {
          success({ blob, width: snapshotWidth, height: snapshotHeight });
        } else {
          reject(new Error('Failed to create image blob'));
        }
      },
      'image/jpeg',
      imageQuality
    )
  );
};
